<template>
  <!-- Modal backdrop with Teleport -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        ref="modalContainer"
        class="fixed inset-0 z-50 overflow-y-auto"
        :aria-labelledby="titleId"
        :aria-describedby="messageId"
        role="dialog"
        aria-modal="true"
        @click="handleBackdropClick"
        @keydown="handleKeydown"
      >
        <!-- Backdrop overlay - Design System V2 -->
        <div
          class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity"
          aria-hidden="true"
        ></div>

        <!-- Modal container -->
        <div class="flex min-h-full items-center justify-center p-4">
          <Transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0 scale-95 translate-y-4"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100 scale-100 translate-y-0"
            leave-to-class="opacity-0 scale-95 translate-y-4"
          >
            <!-- Modal panel - Design System V2 -->
            <div
              v-if="isOpen"
              ref="modalPanel"
              class="relative w-full max-w-md bg-white/95 backdrop-blur-xl border border-slate-200 rounded-2xl shadow-2xl"
              @click.stop
            >
              <!-- Modal content -->
              <div class="p-6">
                <!-- Icon and content row -->
                <div class="flex items-start gap-4">
                  <!-- Icon container -->
                  <div
                    class="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                    :class="typeConfig.iconContainerClass"
                  >
                    <TrashIcon
                      v-if="options.type === 'danger'"
                      class="w-6 h-6"
                      :class="typeConfig.iconClass"
                      aria-hidden="true"
                    />
                    <ExclamationTriangleIcon
                      v-else-if="options.type === 'warning'"
                      class="w-6 h-6"
                      :class="typeConfig.iconClass"
                      aria-hidden="true"
                    />
                    <InformationCircleIcon
                      v-else
                      class="w-6 h-6"
                      :class="typeConfig.iconClass"
                      aria-hidden="true"
                    />
                  </div>

                  <!-- Text content -->
                  <div class="flex-1 min-w-0">
                    <h3
                      :id="titleId"
                      class="text-lg font-semibold text-slate-900"
                    >
                      {{ options.title }}
                    </h3>
                    <p
                      :id="messageId"
                      class="mt-2 text-sm text-slate-600 leading-relaxed"
                    >
                      {{ options.message }}
                    </p>
                  </div>
                </div>

                <!-- Action buttons -->
                <div class="mt-6 flex items-center justify-end gap-3">
                  <!-- Cancel button - Design System V2 Secondary -->
                  <button
                    ref="cancelButton"
                    type="button"
                    class="px-4 py-2.5 text-sm font-semibold border border-slate-300 text-slate-700 bg-white rounded-xl hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2 transition-all duration-200"
                    @click="handleCancel"
                  >
                    {{ options.cancelText }}
                  </button>

                  <!-- Confirm button - Design System V2 with type variants -->
                  <button
                    ref="confirmButton"
                    type="button"
                    class="px-4 py-2.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200"
                    :class="typeConfig.confirmButtonClass"
                    @click="handleConfirm"
                  >
                    {{ options.confirmText }}
                  </button>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
/**
 * ConfirmModal.vue - Global confirmation modal component
 *
 * Applying Vue 3 Composition API patterns from PATTERNS_FROM_BASKET_BASE.md
 * Following Design System V2 from docs/design/DESIGN_SYSTEM_LIGHT.md
 *
 * This component:
 * - Uses Teleport to render in body for proper z-index stacking
 * - Implements focus trap for accessibility
 * - Supports Escape key to cancel
 * - Provides smooth enter/leave transitions
 * - Follows WCAG accessibility guidelines
 *
 * Usage:
 * Mount this component once in App.vue and use the useConfirm composable
 * anywhere in the application for programmatic confirmations.
 *
 * @example
 * // In App.vue
 * <ConfirmModal />
 *
 * // In any component
 * const { confirm } = useConfirm()
 * const confirmed = await confirm({
 *   title: 'Eliminar caso?',
 *   message: 'Esta accion no se puede deshacer.',
 *   type: 'danger'
 * })
 */

import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import { useConfirm, getTypeConfig } from '../../composables/useConfirm.js'
import {
  TrashIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/vue/24/outline'

// Get shared state from composable
const {
  _isOpen: isOpen,
  _options: options,
  handleConfirm,
  handleCancel
} = useConfirm()

// Template refs
const modalContainer = ref(null)
const modalPanel = ref(null)
const cancelButton = ref(null)
const confirmButton = ref(null)

// Unique IDs for accessibility
const titleId = ref(`confirm-title-${Math.random().toString(36).substr(2, 9)}`)
const messageId = ref(`confirm-message-${Math.random().toString(36).substr(2, 9)}`)

// Type configuration
const typeConfig = computed(() => getTypeConfig(options.value.type))

// Body scroll management
const lockBodyScroll = () => {
  document.body.style.overflow = 'hidden'
}

const unlockBodyScroll = () => {
  document.body.style.overflow = ''
}

// Focus management
let previouslyFocusedElement = null

const storeFocus = () => {
  previouslyFocusedElement = document.activeElement
}

const restoreFocus = () => {
  if (previouslyFocusedElement && typeof previouslyFocusedElement.focus === 'function') {
    if (document.contains(previouslyFocusedElement)) {
      previouslyFocusedElement.focus()
    }
    previouslyFocusedElement = null
  }
}

const focusFirstElement = () => {
  nextTick(() => {
    // Focus cancel button by default for safety
    if (cancelButton.value) {
      cancelButton.value.focus()
    }
  })
}

// Keyboard event handlers
const handleKeydown = (event) => {
  // Handle Escape key
  if (event.key === 'Escape') {
    event.preventDefault()
    handleCancel()
    return
  }

  // Focus trap with Tab key
  if (event.key === 'Tab') {
    const focusableElements = [cancelButton.value, confirmButton.value].filter(Boolean)
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (focusableElements.length === 0) {
      event.preventDefault()
      return
    }

    if (event.shiftKey) {
      // Shift+Tab: go backwards
      if (document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      }
    } else {
      // Tab: go forwards
      if (document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }
  }
}

// Handle backdrop click
const handleBackdropClick = (event) => {
  // Only cancel if clicking directly on the backdrop
  if (event.target === modalContainer.value) {
    handleCancel()
  }
}

// Watch for modal open/close
watch(isOpen, (newValue) => {
  if (newValue) {
    storeFocus()
    lockBodyScroll()
    focusFirstElement()
  } else {
    unlockBodyScroll()
    restoreFocus()
  }
})

// Cleanup on unmount
onUnmounted(() => {
  unlockBodyScroll()
})
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Ensure modal is above everything using design system z-index */
[role="dialog"] {
  z-index: var(--z-modal, 1050);
  outline: none;
}

/* Enhanced smooth transitions using design system */
.transition-opacity {
  transition: opacity 200ms cubic-bezier(0.4, 0, 0.2, 1);
}

.transition-all {
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform, opacity;
}

/* Transform utilities with GPU acceleration */
.scale-95 {
  --tw-scale-x: 0.95;
  --tw-scale-y: 0.95;
  transform: translate(var(--tw-translate-x, 0), var(--tw-translate-y, 0)) scale(var(--tw-scale-x), var(--tw-scale-y));
  will-change: transform;
}

.translate-y-4 {
  --tw-translate-y: 1rem;
  transform: translate(var(--tw-translate-x, 0), var(--tw-translate-y)) scale(var(--tw-scale-x, 1), var(--tw-scale-y, 1));
  will-change: transform;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  [role="dialog"] {
    border: 3px solid #000000 !important;
    background: #ffffff !important;
  }

  button {
    border: 2px solid #000000 !important;
    font-weight: 600;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .transition-opacity,
  .transition-all {
    transition: none !important;
  }

  .scale-95,
  .translate-y-4 {
    transform: none !important;
    will-change: auto !important;
  }
}

/* Touch device optimizations */
@media (hover: none) and (pointer: coarse) {
  button {
    min-height: 44px;
    min-width: 44px;
  }
}

/* Print styles */
@media print {
  [role="dialog"] {
    display: none !important;
  }
}
</style>
